import React from "react";

import { linear, type LinearStreamIntegrationDetails } from ".";
import { commitLocalUpdate, type RecordProxy } from "relay-runtime";

import { useSyncedPersistentStateWithOverride } from "~/actionItems/hooks/useSyncedPersistentState";
import { ReactComponent as LinearLogo } from "~/legacy-assets/images/logos/linear.svg";
import { base64Encode } from "~/lib/base64";
import { environment } from "~/models";
import {
	type ActionItemIntegrationCardProps,
	connectStreamIntegration,
	type IntegrationConfigProps,
	StreamSettingsIntegrationCard,
} from "~/stream/StreamSettings/ActionItemIntegrations";
import { Checkbox } from "~/ui";
import { Button } from "~/ui";

import { getIntegrationForKey } from "../actionItemIntegration";
import type { LinearTeamNode } from "./LinearCreateExternalTicketPane";
import { LinearAllColumns } from "./selectors/LinearAllColumns";
import { linearStore } from "./store";

import styles from "./css/styles.module.css";

export function LinearStreamSettingsIntegrationCard({ viewer, onClickIntegration }: ActionItemIntegrationCardProps) {
	const onClick = React.useCallback(() => {
		onClickIntegration(getIntegrationForKey("LINEAR"));
	}, [onClickIntegration]);

	return <StreamSettingsIntegrationCard integration={linear} onClickIntegration={onClick} viewer={viewer} />;
}

export function LinearStreamSettingsConfigView({
	integration,
	stream,
	onFinish,
	onCancel,
	integrationDetails,
}: IntegrationConfigProps<LinearStreamIntegrationDetails>) {
	const [teamId, setTeamId] = useSyncedPersistentStateWithOverride<string | undefined>(
		linearStore,
		"teamId",
		integrationDetails?.teamId,
	);
	const [projectId, setProjectId] = useSyncedPersistentStateWithOverride<string | undefined>(
		linearStore,
		"projectId",
		integrationDetails?.projectId,
	);
	const [cycleId, setCycleId] = useSyncedPersistentStateWithOverride<string | undefined>(
		linearStore,
		"cycleId",
		integrationDetails?.cycleId,
	);
	const [stateId, setStateId] = useSyncedPersistentStateWithOverride<string | undefined>(
		linearStore,
		"stateId",
		integrationDetails?.statusId,
	);
	const [uncheckedStateId, setUncheckedStateId] = useSyncedPersistentStateWithOverride<string | undefined>(
		linearStore,
		"uncheckedStateId",
		integrationDetails?.uncheckedStatusId,
	);
	const [doneStateId, setDoneStateId] = useSyncedPersistentStateWithOverride<string | undefined>(
		linearStore,
		"doneStateId",
		integrationDetails?.doneStatusId,
	);
	const [includeAiGenerated, setIncludeAiGenerated] = React.useState<boolean>(
		integrationDetails?.includeAiGenerated ?? true,
	);

	const handleIncludeAiGeneratedChange = React.useCallback((checked: boolean) => {
		setIncludeAiGenerated(checked);
	}, []);

	const submit = React.useCallback(async () => {
		if (!teamId || !stream || !stream.id) {
			throw new Error("One or more fields are invalid");
		}

		commitLocalUpdate(environment, store => {
			const relayId = base64Encode(`LinearTeamNode:${teamId}`);
			const teamNode = store.get(relayId) as RecordProxy<LinearTeamNode>;
			/* eslint-disable camelcase */
			const destinationInfo = {
				team_id: teamId,
				team_name: teamNode.getValue("name"),
				project_id: projectId,
				cycle_id: cycleId,
				status_id: stateId,
				unchecked_status_id: uncheckedStateId,
				done_status_id: doneStateId,
				include_ai_generated: includeAiGenerated,
			};
			/* eslint-enable camelcase */
			connectStreamIntegration("LINEAR", destinationInfo, stream.id).then(() => {
				onFinish(integration);
			});
		});
	}, [
		teamId,
		stream,
		projectId,
		cycleId,
		stateId,
		uncheckedStateId,
		doneStateId,
		includeAiGenerated,
		onFinish,
		integration,
	]);

	return (
		<>
			<LinearLogo style={{ width: "auto", height: "24px" }} />
			<div className={styles.settingsTitle}>Select a Linear Team</div>
			<p>
				Sync action items between the note series <b>{stream?.displayName}</b> and Linear.
			</p>

			<div className={styles.columnsWrapper}>
				<LinearAllColumns
					teamId={teamId}
					setTeamId={setTeamId}
					projectId={projectId}
					setProjectId={setProjectId}
					cycleId={cycleId}
					setCycleId={setCycleId}
					stateId={stateId}
					setStateId={setStateId}
					uncheckedStateId={uncheckedStateId}
					setUncheckedStateId={setUncheckedStateId}
					doneStateId={doneStateId}
					setDoneStateId={setDoneStateId}
				/>
			</div>

			<div className={styles.toggleOption}>
				<Checkbox
					id="include-ai-generated"
					checked={includeAiGenerated}
					onCheckedChange={handleIncludeAiGeneratedChange}
				>
					Include AI-generated action items
				</Checkbox>
				<span className={styles.toggleDescription}>
					When enabled, AI-generated action items will be sent to this integration.
				</span>
			</div>
			<p>
				Action items <b>created or edited after connecting</b> will be sent to Linear.
			</p>
			<div className={styles.buttonRow}>
				<Button size="normal" onClick={onCancel}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={submit} disabled={!teamId}>
					Connect
				</Button>
			</div>
		</>
	);
}
